import { canTestChat } from '../agents/chatConfiguration';

export const MODES = ['voice', 'chat'];
export const FIELD_TYPES = ['text', 'textarea', 'email', 'phone', 'number', 'select', 'multiselect', 'checkbox'];
const keyPattern = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;
const variablePattern = /^[A-Za-z_][A-Za-z0-9_]{0,49}$/;
export const newDirectRoute = () => ({ type: 'direct', agent_id: '' });
const configurationKey = prefix => `${prefix}_${crypto.randomUUID().replace(/-/g, '')}`;
export const newRouteOption = () => ({ key: configurationKey('option'), label: '', next: newDirectRoute() });
export const newMenuRoute = () => ({ type: 'menu', key: configurationKey('menu'), label: '', options: [newRouteOption()] });
export const newWidgetField = modes => ({
  key: configurationKey('field'), label: '', type: 'text', required: false,
  modes: [...modes], variable_mapping: '',
});
export const TOPIC_ICONS = [
  { value: 'calendar', label: 'Calendar' }, { value: 'doctor', label: 'Doctor' }, { value: 'report', label: 'Report' },
  { value: 'lab', label: 'Lab' }, { value: 'heart', label: 'Heart' }, { value: 'pill', label: 'Medicine' },
  { value: 'shield', label: 'Insurance' }, { value: 'billing', label: 'Billing' }, { value: 'location', label: 'Location' }, { value: 'help', label: 'Help' },
];
// Reserved for the tapped start topic, which a browser call receives.
const TOPIC_FIELD = 'start_topic';
export const MAX_TOPICS = 6;
const phonePattern = /^\+[1-9]\d{6,14}$/;
export const newTopic = () => ({ key: configurationKey('topic'), label: '', icon: 'calendar' });
export const newWidgetConfiguration = () => ({
  schema_version: 1, enabled: 'No', description: '', collect_phone_number: 'No',
  appearance: { title: 'Ava', theme: 'system', primary_color: '#0E7C7B', position: 'bottom-right', avatar_path: null },
  modes: { voice: 'Yes', chat: 'Yes' }, fields: [],
  routing: { chat: newDirectRoute(), voice: newDirectRoute() },
  topics: [], emergency_line: null, front_desk_line: null, organization: {},
  voice_entry: { type: 'web_call' },
});

// Saved drafts may predate Care Messenger v2: drop the default mode and URL avatar.
export const normalizeWidget = value => {
  const defaults = newWidgetConfiguration();
  const { channels, default_channel, default_mode: _mode, languages: _languages, ...config } = value || {};
  const { avatar_url: _avatar, ...appearance } = config.appearance || {};
  return {
    ...defaults, ...config,
    modes: config.modes ?? channels ?? defaults.modes,
    routing: config.routing ?? {},
    fields: (config.fields || []).map(({ channels: legacyModes, ...field }) => ({ ...field, modes: field.modes ?? legacyModes ?? [] })),
    appearance: { ...defaults.appearance, ...appearance },
    organization: config.organization || {},
    voice_entry: config.voice_entry || defaults.voice_entry,
  };
};

export const enabledModes = config => MODES.filter(mode => (config.modes ?? config.channels)?.[mode] === 'Yes');
export const isArchived = agent => (
  agent?.status === 'archived' || agent?.archived === true
  || agent?.is_archived === true || agent?.is_archived === 'Yes'
);
export const isEligibleTarget = (agent, mode) => (
  !isArchived(agent) && agent?.is_active === true
  && (mode === 'voice' || canTestChat(agent))
);

// Chat and browser calls need an agent route; a phone-number call does not.
export const sessionModes = config => enabledModes(config).filter(mode => !(mode === 'voice' && config.voice_entry?.type === 'phone_number'));
// Phone-number calls do not use an agent route.
const withRoutes = config => ({ ...config, routing: Object.fromEntries(sessionModes(config)
  .map(mode => [mode, config.routing?.[mode] || newDirectRoute()])) });

// Disabled routes must not be posted. Keep fields visible for explicit mode reassignment.
export const changeWidgetModes = (config, modes) => withRoutes({
  ...config,
  modes: Object.fromEntries(MODES.map(mode => [mode, modes.includes(mode) ? 'Yes' : 'No'])),
  fields: config.fields.map(field => ({ ...field, modes: field.modes.filter(mode => modes.includes(mode)) })),
});
export const routingOptions = route => {
  const options = new Map();
  const walk = node => node?.type === 'menu' && node.options.forEach(option => {
    if (option.key && option.label && !options.has(option.key)) options.set(option.key, option.label);
    walk(option.next);
  });
  walk(route);
  return [...options].map(([value, label]) => ({ value, label }));
};
export const changeVoiceEntry = (config, voiceEntry) => withRoutes({ ...config, voice_entry: voiceEntry });

export const validateWidget = value => {
  const config = normalizeWidget(value);
  const errors = {};
  const text = (value, path) => {
    if (typeof value !== 'string' || !value.trim() || value.length > 500) errors[path] = 'Enter 1–500 characters.';
  };
  const key = (value, path) => { if (!keyPattern.test(value || '')) errors[path] = 'Start with a letter; use up to 64 letters, digits, underscores or hyphens.'; };
  if (config.schema_version !== 1) errors.schema_version = 'Unsupported widget schema version.';
  if (!['Yes', 'No'].includes(config.enabled)) errors.enabled = 'Use Yes or No.';
  MODES.forEach(mode => { if (!['Yes', 'No'].includes(config.modes?.[mode])) errors[`modes.${mode}`] = 'Use Yes or No.'; });
  const modes = enabledModes(config);
  if (!modes.length) errors.modes = 'Enable at least one mode.';
  if (typeof config.description !== 'string' || config.description.length > 2000) errors.description = 'Enter up to 2,000 characters.';
  text(config.appearance.title, 'appearance.title');
  if (!['light', 'dark', 'system'].includes(config.appearance.theme)) errors['appearance.theme'] = 'Choose a theme.';
  if (!['bottom-left', 'bottom-right'].includes(config.appearance.position)) errors['appearance.position'] = 'Choose a position.';
  if (!/^#[0-9a-fA-F]{6}$/.test(config.appearance.primary_color)) errors['appearance.primary_color'] = 'Use a six-digit hex color.';
  const phone = (value, path, label) => { if (!phonePattern.test(value || '')) errors[path] = `${label}: use the international format, e.g. +918012345678.`; };
  const short = (value, path, label) => { if (typeof value !== 'string' || !value.trim() || value.length > 120) errors[path] = `${label}: enter 1–120 characters.`; };
  const topics = config.topics || [];
  if (topics.length > MAX_TOPICS) errors.topics = `Use at most ${MAX_TOPICS} start topics.`;
  const topicKeys = new Set();
  topics.forEach((topic, index) => {
    key(topic.key, `topics.${index}.key`);
    if (topicKeys.has(topic.key)) errors[`topics.${index}.key`] = 'Topic keys must be unique.';
    topicKeys.add(topic.key);
    if (typeof topic.label !== 'string' || !topic.label.trim() || topic.label.length > 60) errors[`topics.${index}.label`] = 'Topic label: enter 1–60 characters.';
    if (!TOPIC_ICONS.some(icon => icon.value === topic.icon)) errors[`topics.${index}.icon`] = 'Choose a topic icon.';
  });
  if (config.emergency_line) { short(config.emergency_line.label, 'emergency_line.label', 'Emergency line name'); phone(config.emergency_line.number, 'emergency_line.number', 'Emergency number'); }
  if (config.front_desk_line) phone(config.front_desk_line.number, 'front_desk_line.number', 'Front-desk number');
  if (config.organization?.name) short(config.organization.name, 'organization.name', 'Hospital name');
  if (config.organization?.privacy_policy_url && !/^https:\/\/[^\s]+$/.test(config.organization.privacy_policy_url)) errors['organization.privacy_policy_url'] = 'Use an HTTPS privacy policy link.';
  const voiceEntry = config.voice_entry || {};
  if (!['web_call', 'phone_number'].includes(voiceEntry.type)) errors['voice_entry.type'] = 'Choose how calls start.';
  if (voiceEntry.phone_number || voiceEntry.type === 'phone_number') phone(voiceEntry.phone_number, 'voice_entry.phone_number', 'Call number');
  const fields = config.fields || [];
  if (!['Yes', 'No'].includes(config.collect_phone_number)) errors.collect_phone_number = 'Use Yes or No.';
  if (config.collect_phone_number === 'Yes' && fields.some(field => field.key === 'customer_phone_number' || field.variable_mapping === 'customer_phone_number')) errors.fields = 'Remove the custom customer phone field before enabling built-in phone collection.';
  if (fields.length + (config.collect_phone_number === 'Yes' && sessionModes(config).length ? 1 : 0) + (sessionModes(config).includes('voice') ? 1 : 0) > 50) errors.fields = 'Use at most 50 fields including built-in phone and topic fields.';
  if (fields.some(field => field.key === TOPIC_FIELD || field.variable_mapping === TOPIC_FIELD)) errors.fields = 'start_topic is reserved for the tapped start topic.';
  const keys = new Set();
  const mappings = { voice: new Set(), chat: new Set() };
  fields.forEach((field, index) => {
    const path = `fields.${index}`;
    key(field.key, `${path}.key`);
    if (keys.has(field.key)) errors[`${path}.key`] = 'Field keys must be unique.';
    keys.add(field.key);
    text(field.label, `${path}.label`);
    if (!FIELD_TYPES.includes(field.type)) errors[`${path}.type`] = 'Choose a field type.';
    if (typeof field.required !== 'boolean') errors[`${path}.required`] = 'Required must be true or false.';
    if (!field.modes?.length || field.modes.some(mode => !modes.includes(mode)) || new Set(field.modes).size !== field.modes.length) errors[`${path}.modes`] = 'Choose at least one enabled mode, without duplicates.';
    if (!variablePattern.test(field.variable_mapping || '')) errors[`${path}.variable_mapping`] = 'Use a variable name of up to 50 letters, digits or underscores; do not start with a digit.';
    if (field.variable_mapping?.startsWith('system_')) errors[`${path}.variable_mapping`] = 'The system_ prefix is reserved. Choose a custom variable name.';
    (field.modes || []).forEach(mode => {
      if (mappings[mode]?.has(field.variable_mapping)) errors[`${path}.variable_mapping`] = 'Variable mappings must be unique within each mode.';
      mappings[mode]?.add(field.variable_mapping);
    });
    const options = field.options || [];
    if (['select', 'multiselect'].includes(field.type)) {
      if (!options.length || options.length > 100) errors[`${path}.options`] = 'Select fields require 1–100 options.';
      const values = new Set();
      options.forEach((option, optionIndex) => {
        key(option.value, `${path}.options.${optionIndex}.value`);
        text(option.label, `${path}.options.${optionIndex}.label`);
        if (values.has(option.value)) errors[`${path}.options.${optionIndex}.value`] = 'Option values must be unique.';
        values.add(option.value);
      });
    } else if (options.length) errors[`${path}.options`] = 'Only select and multiselect fields accept options.';
  });
  const route = (node, path, depth, menuKeys) => {
    if (node?.type === 'direct') {
      if (!node.agent_id?.trim() || node.agent_id.length > 100) errors[`${path}.agent_id`] = 'Select a routing agent.';
    } else if (node?.type === 'menu') {
      if (depth >= 2) { errors[path] = 'Use at most two menu levels.'; return; }
      key(node.key, `${path}.key`); text(node.label, `${path}.label`);
      if (menuKeys.has(node.key)) errors[`${path}.key`] = 'Menu keys must be unique within the mode.';
      menuKeys.add(node.key);
      if (!node.options?.length || node.options.length > 20) errors[`${path}.options`] = 'Menus require 1–20 options.';
      const optionKeys = new Set();
      (node.options || []).forEach((option, index) => {
        const optionPath = `${path}.options.${index}`;
        key(option.key, `${optionPath}.key`); text(option.label, `${optionPath}.label`);
        if (optionKeys.has(option.key)) errors[`${optionPath}.key`] = 'Option keys must be unique within this menu.';
        optionKeys.add(option.key);
        route(option.next, `${optionPath}.next`, depth + 1, menuKeys);
      });
    } else errors[path] = 'Choose a direct agent or a menu.';
  };
  const routed = sessionModes(config);
  if (Object.keys(config.routing || {}).some(mode => !modes.includes(mode))) errors.routing = 'Only enabled modes may have routes.';
  routed.forEach(mode => route(config.routing?.[mode], `routing.${mode}`, 0, new Set()));
  return errors;
};

export function getWidgetAccess(role) {
  const normalized = String(role || '').toLowerCase();
  return { read: ['superadmin', 'admin', 'manager'].includes(normalized), write: ['superadmin', 'admin'].includes(normalized) };
}

export function readDashboardIdentity() {
  try {
    const cookie = document.cookie.split('; ').find(value => value.startsWith('dashboard_login_user_creds='));
    return cookie ? JSON.parse(cookie.slice(cookie.indexOf('=') + 1)) : {};
  } catch { return {}; }
}

export async function readWidgetResponse(response) {
  const body = await response.json().catch(() => ({}));
  if (response.ok || (response.status >= 200 && response.status < 300)) return body;
  const fallback = {
    401: 'Your session has expired. Sign in again.', 403: 'You do not have permission to access these settings.',
    404: 'This widget or API is unavailable.', 409: 'This widget changed since you opened it. Reload the saved version before saving again.',
    503: 'Widget configuration is unavailable. Please try again later.',
  };
  const error = new Error(body.error?.message || (typeof body.error === 'string' ? body.error : null) || body.message || fallback[response.status] || 'Unable to complete the request.');
  error.status = response.status;
  error.code = body.error?.code;
  error.details = body.error?.details;
  throw error;
}
